import React, { useContext, useEffect, useState } from 'react';

import { StyleProp, StyleSheet, View } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import DyteParticipantTile from './DyteParticipantTile';
import { defaultConfig } from '../lib/default-ui-config';
import { UIConfig } from '../types/ui-config';
import { Peer } from '../types/dyte-client';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { States } from '../types/props';
import { useGridItemDimensions } from '../utils/grid';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function DyteSimpleGrid({
  aspectRatio = '3:4',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  gap = 8,
  iconPack = defaultIconPack,
  meeting,
  participants,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = 'sm',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  states,
  t,
  style,
}: {
  aspectRatio?: string;
  config?: UIConfig;
  gap?: number;
  iconPack?: IconPack;
  meeting: DyteClient;
  participants: Peer[];
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  t?: DyteI18n;
  style?: StyleProp<any>;
}) {
  const [participantList, setParticipantList] = useState(participants);
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const calcVals = useGridItemDimensions({
    count: participantList.length,
    dimensions: {
      height: style?.height ?? dimensions.height,
      width: style?.width ?? dimensions.width,
    },
    aspectRatio: aspectRatio,
    gap: gap,
  });
  useEffect(() => {
    setParticipantList(participants);
  }, [participants]);
  const isLastRow = (index) => {
    return (
      index >
      participantList.length - 1 - (participantList.length % calcVals.cols)
    );
  };
  const noOfParticipantinLastRow = () => {
    return participantList.length % calcVals.cols !== 0
      ? participantList.length % calcVals.cols
      : calcVals.cols;
  };
  const getSplitIndex = () => {
    const lastRowPeers = noOfParticipantinLastRow();
    return participantList.length - 1 - lastRowPeers;
  };
  const getLastRowWidth = () => {
    return calcVals.width * (calcVals.cols / noOfParticipantinLastRow());
  };
  const styles = StyleSheet.create({
    gridContainer: {
      ...style,
      flex: 1,
      flexDirection: 'row',
      // flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background[1000],
    },
    gridStyle: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
  return (
    <View
      style={styles.gridContainer}
      onLayout={(event) => {
        const w = event.nativeEvent.layout.width;
        const h = event.nativeEvent.layout.height;
        if (w !== 0 && h !== 0)
          setDimensions({
            width: event.nativeEvent.layout.width,
            height: event.nativeEvent.layout.height,
          });
      }}
    >
      <View style={styles.gridStyle}>
        {participantList.length > 1 &&
          participantList.slice(0, getSplitIndex() + 1).map((p, index) => (
            <DyteParticipantTile
              meeting={meeting}
              key={index}
              participant={p}
              style={{
                width:
                  isLastRow(index) &&
                  participantList.length % calcVals.cols !== 0
                    ? (calcVals.cols /
                        (participantList.length % calcVals.cols)) *
                      calcVals.width
                    : calcVals.width,
                height: calcVals.height,
              }}
              iconPack={iconPack}
              t={t}
            />
          ))}
        {participantList.length > 1 &&
          participantList
            .slice(-1 * noOfParticipantinLastRow())
            .map((p, index) => (
              <DyteParticipantTile
                meeting={meeting}
                key={index}
                participant={p}
                style={{
                  width: getLastRowWidth(),
                  height: calcVals.height,
                }}
                iconPack={iconPack}
                t={t}
              />
            ))}
        {participantList.length === 1 && (
          <DyteParticipantTile
            meeting={meeting}
            participant={participantList[participantList.length - 1]}
            style={{
              width: calcVals.width,
              height: calcVals.height,
            }}
            iconPack={iconPack}
            t={t}
          />
        )}
      </View>
    </View>
  );
}
